import React from 'react';
import SectionCard from './SectionCard.jsx';
import FormalStatementTable from './FormalStatementTable.jsx';
import {
  DENSITY_LABELS, normalizePeriodScalar,
  getTabDirection, TAB_DEFAULT_DIRECTION, WINDOW_DIRECTIONS,
  VERBOSITY_LEVELS, VERBOSITY_LABELS, VERBOSITY_HINTS, getVerbosity,
} from '../engine.js';

/* v7.3.1 — FormalStatementSection
   Thin wrapper around SectionCard + FormalStatementTable. */
export default function FormalStatementSection({
  tabId, stmtKey,
  label, subtitle,
  sections, entity, period, title,
  scenario, setScenario,
  periods, periodPicker,
  currentPeriodLabel,
  currentPeriodProof,
  sourceState,
  ledgerAggregateRowProof = false,
  forceOpen = false,
}) {
  const savedOpen = scenario?.formalStatementsOpen?.[tabId]?.[stmtKey] ?? false;
  const open = forceOpen || savedOpen;

  const toggle = () => {
    if (forceOpen) return;
    const next = { ...(scenario.formalStatementsOpen ?? {}) };
    next[tabId] = { ...(next[tabId] ?? {}), [stmtKey]: !open };
    setScenario({ ...scenario, formalStatementsOpen: next });
  };

  const header = (
    <button
      type="button"
      className="bg-transparent border-0 text-[inherit] font-[inherit] font-semibold p-0 cursor-pointer text-left w-full inline-flex items-center gap-[0.35rem] hover:text-accent-gold"
      onClick={toggle}
      aria-expanded={open}
      title={forceOpen ? 'Focused statement route' : open ? 'Click to collapse' : 'Click to expand'}
    >
      {open ? '▾' : '▸'} {label}
    </button>
  );

  const enrichedSubtitle = periodPicker
    ? (
        <>
          {subtitle}
          {' '}{periodPicker}
        </>
      )
    : subtitle;

  return (
    <SectionCard
      title={header}
      subtitle={enrichedSubtitle}
    >
      {open && (
        <div
          key={`${periods?.length ?? 0}-${periods?.[0] ?? ''}-${periods?.[periods?.length - 1] ?? ''}-${currentPeriodLabel ?? ''}`}
          className="animate-stmt-recalc"
        >
          <FormalStatementTable
            title={title}
            entity={entity}
            period={period}
            periods={periods}
            sections={sections}
            sparklineGradient={scenario?.sparklineGradient ?? false}
            showTrendColumn={scenario?.showTrendColumn !== false}
            showRowSparkline={scenario?.showRowSparkline !== false}
            semanticTone={!!scenario?.sparklineSemanticTone}
            guides={scenario?.guides}
            currentPeriodLabel={currentPeriodLabel}
            currentPeriodProof={currentPeriodProof}
            sourceState={sourceState}
            ledgerAggregateRowProof={ledgerAggregateRowProof}
          />
        </div>
      )}
    </SectionCard>
  );
}

/* v8 — PeriodPicker: chooses how many periods (columns) to show.
   Global (scalar) — no longer per-tab. Now rendered once in the Topbar. */
export function PeriodPicker({ scenario, setScenario, options = [1, 2, 3, 4, 6, 12] }) {
  const count = normalizePeriodScalar(scenario?.formalPeriodCount, 12);
  const set = (n) => setScenario({ ...scenario, formalPeriodCount: n });
  const base = 'bg-surface border border-border text-text-dim px-[9px] py-[1px] text-[0.72rem] rounded-[3px] cursor-pointer font-[inherit] transition-[color,border-color,background] duration-100 hover:text-text hover:border-border-strong';
  const active = '!bg-accent-gold !text-bg !border-accent-gold font-semibold shadow-[0_0_0_1px_rgba(240,180,41,0.35)]';
  return (
    <span className="inline-flex items-center gap-[2px] align-middle" role="group" aria-label="Comparative period count">
      {options.map((n) => (
        <button
          key={n}
          type="button"
          className={`${base} ${count === n ? active : ''}`}
          onClick={() => set(n)}
          aria-pressed={count === n}
        >
          {n}
        </button>
      ))}
      <span className="text-[0.7rem] text-text-dim ml-1">periods</span>
    </span>
  );
}

/* DirectionPicker: Past | Present | Future per tab.
   `activeTab` selects which tab's override to write (e.g., 'actuals'). Each
   tab has a discipline-appropriate default (TAB_DEFAULT_DIRECTION); the
   picker highlights the effective direction including overrides. Hidden
   on tabs that don't consume it (e.g., 'settings'). Labels use the
   time-noun triad; table subtitles use the parallel window-behavior triad
   (trailing / spanning / leading) — see each tab's dirLabel. */
const DIRECTION_LABELS = { past: 'Past', present: 'Present', future: 'Future' };
const DIRECTION_HINTS = {
  past:    'Trailing window — historical backcast',
  present: 'Spanning window — past + future overlap',
  future:  'Leading window — projection',
};
export function DirectionPicker({ scenario, setScenario, activeTab }) {
  if (!activeTab || !TAB_DEFAULT_DIRECTION[activeTab]) return null;
  const current = getTabDirection(scenario, activeTab);
  const set = (d) => {
    const next = { ...(scenario.windowDirection ?? {}) };
    next[activeTab] = d;
    setScenario({ ...scenario, windowDirection: next });
  };
  const base = 'bg-surface border border-border text-text-dim px-[9px] py-[1px] text-[0.72rem] rounded-[3px] cursor-pointer font-[inherit] transition-[color,border-color,background] duration-100 hover:text-text hover:border-border-strong';
  const active = '!bg-accent-green !text-bg !border-accent-green font-semibold shadow-[0_0_0_1px_rgba(52,211,153,0.35)]';
  return (
    <span className="inline-flex items-center gap-[2px] align-middle" role="group" aria-label="Window direction">
      {WINDOW_DIRECTIONS.map((d) => (
        <button
          key={d}
          type="button"
          className={`${base} ${current === d ? active : ''}`}
          onClick={() => set(d)}
          aria-pressed={current === d}
          title={DIRECTION_HINTS[d]}
        >
          {DIRECTION_LABELS[d]}
        </button>
      ))}
    </span>
  );
}

/* v8 Path L — VerbosityPicker: minimal / standard / rich.
   Universal user-type toggle. Executive → minimal headline only.
   Analyst → standard KPIs + sparklines. Power user → every section,
   every sparkline, every rich tooltip shown by default. */
export function VerbosityPicker({ scenario, setScenario }) {
  const current = getVerbosity(scenario);
  const set = (v) => setScenario({ ...scenario, verbosity: v });
  const base = 'bg-surface border border-border text-text-dim px-[9px] py-[1px] text-[0.72rem] rounded-[3px] cursor-pointer font-[inherit] transition-[color,border-color,background] duration-100 hover:text-text hover:border-border-strong';
  const active = '!bg-accent-blue !text-white !border-accent-blue font-semibold shadow-[0_0_0_1px_rgba(96,165,250,0.35)]';
  return (
    <span className="inline-flex items-center gap-[2px] align-middle" role="group" aria-label="Verbosity level">
      {VERBOSITY_LEVELS.map((v) => (
        <button
          key={v}
          type="button"
          className={`${base} ${current === v ? active : ''}`}
          onClick={() => set(v)}
          aria-pressed={current === v}
          title={VERBOSITY_HINTS[v]}
        >
          {VERBOSITY_LABELS[v]}
        </button>
      ))}
    </span>
  );
}

/* v8 — DensityPicker: months per period. Global scalar. */
export function DensityPicker({ scenario, setScenario, options = [1, 2, 3, 4, 6, 12] }) {
  const density = normalizePeriodScalar(scenario?.formalPeriodDensity, 1);
  const set = (n) => setScenario({ ...scenario, formalPeriodDensity: n });
  const base = 'bg-surface border border-border text-text-dim px-[9px] py-[1px] text-[0.72rem] rounded-[3px] cursor-pointer font-[inherit] transition-[color,border-color,background] duration-100 hover:text-text hover:border-border-strong whitespace-nowrap';
  const active = '!bg-accent-blue !text-white !border-accent-blue font-semibold shadow-[0_0_0_1px_rgba(96,165,250,0.35)]';
  return (
    <span className="inline-flex items-center gap-[2px] align-middle" role="group" aria-label="Period density (months per period)">
      {options.map((n) => (
        <button
          key={n}
          type="button"
          className={`${base} ${density === n ? active : ''}`}
          onClick={() => set(n)}
          aria-pressed={density === n}
          title={`${DENSITY_LABELS[n] || `${n}-month`} — ${n} month${n === 1 ? '' : 's'} per period`}
        >
          {DENSITY_LABELS[n] || `${n}mo`}
        </button>
      ))}
    </span>
  );
}
